import { useLocation } from 'react-router';
import { useToggle } from 'ahooks';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { NoSearchResults } from '@modules/professional/component/shared/NoSearchResults';
import type { DefaultElasticSearchResults } from '@modules/professional/type';
import { SearchFilter } from '@modules/professional/component/shared/SearchFilter';
import { CollapsiblePageLayout } from '~/modules/category/component/collapsible-page-layout/CollapsiblePageLayout';
import { useSearchFilters } from '@modules/search/hook/use-search-filters';
import { useMatchesHandleData } from '~/modules/root/hook';
import type { SearchIndex } from '~/modules/search/type';
import { IN_PAGE_SEARCH_IGNORE_FILTER_KEYS } from '~/modules/professional/config/constants';
import { CategoryFilterNav } from '@modules/category/component/category-filter-section/CategoryFilterNav';
import AppliedFilters from '~/modules/search/components/search-filter-section/AppliedFilters';
import { resetFilterStates } from '~/modules/search/util/reset-filter-states';
import { CategoryPageTileWithAdminActions } from '~/ui-rocco/pages/category-page/CategoryPageTileWithAdminActions';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import type { ProfessionalTileViewModel as ProfessionalTileViewModelRocco } from '@rocco/ui/tile-professional/view-models/professional';
import { Grid } from '@rocco/ui/layout/components/Grid';
import { Pagination as TilePagination } from '@rocco/ui/pagination/components/Pagination';
import { buildPaginationUrl } from '~/modules-rocco/pagination/util/build-pagination-url';

interface PaginatedProfessionalsProp extends DefaultElasticSearchResults {
    professionalTilesRocco: TileViewModel<ProfessionalTileViewModelRocco>[];
    disableTileLink?: boolean;
    disableSaveToDesignBoard?: boolean;
    disableSort?: boolean;
}

export const PaginatedNetwork: React.FC<PaginatedProfessionalsProp> = ({
    pagination,
    professionalTilesRocco,
    filterStates,
    disableSaveToDesignBoard,
    disableSort,
}) => {
    const appDisplayMode = useAppDisplayModeContext();
    const { desktop } = appDisplayMode;

    const isBMPreview = useMatchesHandleData('isBMPreview', false);

    const [filterDrawerOpen, { toggle: toggleFilterDrawer }] =
        useToggle(!disableSort);
    const remixLocation = useLocation();

    const tiles = professionalTilesRocco.map((tile) => {
        return (
            <div
                key={tile.model.id}
                className="transform-gpu transition-transform duration-300"
            >
                <CategoryPageTileWithAdminActions
                    tile={{
                        ...tile,
                        disableDesignBoard:
                            isBMPreview || disableSaveToDesignBoard,
                    }}
                />
            </div>
        );
    });

    const ignoreFilterKeys = IN_PAGE_SEARCH_IGNORE_FILTER_KEYS;
    const category = useMatchesHandleData<SearchIndex>('category', 'project');

    const searchFilters = useSearchFilters({
        category,
        initialStates: filterStates,
        isCategoryPage: false,
        ignoreFilterKeys,
    });

    return (
        <div>
            <div className="mx-12.5 hidden h-[1px] bg-black md:block qhd:px-0"></div>

            <div className="md:mt-16 md:pb-47.5">
                <CollapsiblePageLayout
                    sidePanel={
                        desktop && (
                            <div className="md:pt-15">
                                <CategoryFilterNav
                                    category="professional"
                                    searchFilters={searchFilters}
                                    hideCategorySectionTitle={false}
                                />
                            </div>
                        )
                    }
                    hideSidePanel={!filterDrawerOpen}
                >
                    <div className="md:h-15">
                        <SearchFilter
                            searchFilters={searchFilters}
                            filterDrawerOpen={filterDrawerOpen}
                            toggleFilterDrawer={toggleFilterDrawer}
                            disableSort={disableSort}
                            disableToggleFilter={true}
                        />
                    </div>
                    {searchFilters.appliedFilters &&
                        searchFilters.appliedFilters.length > 0 && (
                            <AppliedFilters
                                filterItems={searchFilters.appliedFilters}
                                resetFilters={() =>
                                    searchFilters.resetFilters(
                                        resetFilterStates(
                                            searchFilters.filterStates,
                                            [...ignoreFilterKeys, 'category'],
                                        ),
                                    )
                                }
                            />
                        )}
                    <div>
                        {!tiles.length ? (
                            <NoSearchResults resultsType={'professionals'} />
                        ) : (
                            <>
                                <Grid
                                    colClasses={'grid-cols-1 lg:grid-cols-3'}
                                    rowsGapClass="gap-x-2 gap-y-12 md:gap-x-6 md:gap-y-20"
                                >
                                    {tiles}
                                </Grid>

                                {pagination.totalPages > 1 && (
                                    <div className="pt-[6.5625rem] md:pt-20">
                                        <TilePagination
                                            currentPath={remixLocation}
                                            current={pagination.page}
                                            total={pagination.totalPages}
                                            urlBuilder={buildPaginationUrl}
                                            visibleCount={
                                                appDisplayMode.desktop ? 4 : 3
                                            }
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </CollapsiblePageLayout>
            </div>
        </div>
    );
};
